/**
 * ClientDetailModal — opened by clicking a client row on the planner.
 *
 * Phase 1 scope: move the client to another open month (Adviser-manual —
 * the only way review_month ever changes, per the permanent-anchor
 * principle in §34.4), work the four-row checklist, and remove the
 * client from the planner. Status is a READ-ONLY chip — status
 * transitions (add to pipeline / complete) belong to Phase 2 and are
 * deliberately not built here.
 *
 * Checklist model (fixed order): Annual Review (always applicable),
 * Section 14 Fee Renewal (per-client toggle), Living Annuity Income
 * Review (per-client toggle), Commission Splits (always applicable).
 * Turning an applicability toggle off resets that item's done flag.
 * All changes persist immediately with optimistic UI via the parent's
 * onPatch handler.
 *
 * Removal is a hard DELETE behind a confirm step — a deliberate,
 * Adviser-only exception for planner entries only (planning data, not
 * business records; CLAUDE.md §34.6).
 */

import React, { useState } from 'react';
import { Building2, Trash2, CalendarPlus, Pencil } from 'lucide-react';
import {
  MONTH_NAMES,
  OPEN_MONTHS,
  CHECKLIST_ITEMS,
  REVIEW_STATUS_LABELS,
  formatClientListName,
  isReviewOverdue,
  getCurrentSASTYearMonth,
  normaliseNameInput,
  deriveDisplayName,
} from '../../utils/reviewPlanner';

// Phase 2 status set (§34.9). The chip stays read-only here — the
// planner offers no status transitions; those live on the calendar card
// modal and the nudge. The one exception is "Add to pipeline" below,
// which performs the planned → submitted transition ad hoc.
const STATUS_CHIP_STYLES = {
  planned: { background: 'var(--cp-surface-alt)', color: 'var(--cp-text-muted)' },
  submitted: { background: 'rgba(27, 154, 170, 0.12)', color: 'var(--cp-secondary)' },
  appointment_made: { background: 'rgba(244, 162, 97, 0.18)', color: 'var(--cp-accent-dark)' },
  completed: { background: 'rgba(46, 196, 182, 0.15)', color: 'var(--cp-success)' },
};

function ChecklistRow({ item, client, onPatch }) {
  // Entities never render the S14 / LA rows at all (structural rule,
  // §34.10) — the caller filters them out before rendering this row.
  const hasToggle = !!item.applicableField;
  const applicable = !hasToggle || client[item.applicableField];
  const done = client[item.doneField];

  const setApplicable = (next) => {
    const patch = { [item.applicableField]: next };
    // Switching an item off also resets its done flag — a not-applicable
    // item can never be left "done".
    if (!next) patch[item.doneField] = false;
    onPatch(patch);
  };

  return (
    <div className={`rv-check-row${!applicable ? ' rv-check-muted' : ''}`}>
      {hasToggle ? (
        <button
          type="button"
          className={`rv-toggle${applicable ? ' rv-toggle-on' : ''}`}
          onClick={() => setApplicable(!applicable)}
          aria-pressed={applicable}
          aria-label={`${item.label} applicable`}
        />
      ) : (
        <span style={{ width: '34px', flexShrink: 0 }} />
      )}
      <span className="rv-check-label">
        {item.label}{' '}
        <span className="rv-check-sub">
          {hasToggle ? (applicable ? '' : 'not applicable') : 'always'}
        </span>
      </span>
      <input
        type="checkbox"
        className="rv-checkbox"
        checked={applicable && done}
        disabled={!applicable}
        onChange={(e) => onPatch({ [item.doneField]: e.target.checked })}
        aria-label={`${item.label} done`}
      />
    </div>
  );
}

/**
 * Inline name editor (§34.2) — collapsed behind the pencil next to the
 * title. Three shapes:
 *   - structured INDIVIDUAL (has client_surname): Surname / Initials /
 *     Nickname; display_name is regenerated by the import derivation on
 *     save, so the natural name and the surname-first list format update
 *     everywhere at once.
 *   - MANUAL individual (no client_surname): the same three fields
 *     (blank) PLUS a Display name field. Filling Surname upgrades the
 *     client to structured (display becomes derived); leaving the
 *     structured fields blank saves the edited Display name verbatim.
 *   - ENTITY: one Name field, written to BOTH client_surname and
 *     display_name (the import convention keeps them identical).
 * Saves are per-client — never group-expanded. Names already
 * snapshotted into a sent prep request's member_sections are records of
 * what was sent and are deliberately NOT updated (§34.11).
 */
function NameEditor({ client, onSave, onCancel, saving }) {
  const isManual = !client.isEntity && !client.clientSurname;
  const [surname, setSurname] = useState(client.clientSurname || '');
  const [initials, setInitials] = useState(client.clientInitials || '');
  const [nickname, setNickname] = useState(client.clientNickname || '');
  const [displayName, setDisplayName] = useState(client.displayName || '');
  const [entityName, setEntityName] = useState(
    client.isEntity ? (client.displayName || client.clientSurname || '') : ''
  );
  const [error, setError] = useState('');

  const cleanSurname = normaliseNameInput(surname);
  const cleanInitials = normaliseNameInput(initials);
  const cleanNickname = normaliseNameInput(nickname);
  // Once a surname is present the display name is derived, not typed.
  const structured = !client.isEntity && !!cleanSurname;
  const derivedPreview = deriveDisplayName({
    surname: cleanSurname,
    initials: cleanInitials,
    nickname: cleanNickname,
    isEntity: false,
  });

  const handleSave = () => {
    setError('');
    if (client.isEntity) {
      const name = normaliseNameInput(entityName);
      if (!name) {
        setError('The name cannot be empty.');
        return;
      }
      // Entity convention: surname and display name are the same value.
      onSave({ clientSurname: name, displayName: name });
      return;
    }

    const cleanDisplay = normaliseNameInput(displayName);
    if (!cleanSurname && !cleanNickname && !cleanDisplay) {
      setError('Enter at least a surname, a nickname, or a display name.');
      return;
    }
    if (structured || !isManual) {
      // Structured save (also the manual→structured upgrade path):
      // persist the three columns and regenerate display_name.
      onSave({
        clientSurname: cleanSurname,
        clientInitials: cleanInitials,
        clientNickname: cleanNickname,
        displayName: deriveDisplayName({
          surname: cleanSurname,
          initials: cleanInitials,
          nickname: cleanNickname,
          isEntity: false,
        }),
      });
    } else {
      // Manual client, structured fields left blank: the edited display
      // name is saved verbatim.
      onSave({ displayName: cleanDisplay });
    }
  };

  const field = (label, value, setter, placeholder = '') => (
    <div style={{ flex: '1 1 110px', minWidth: '110px' }}>
      <label className="rv-label">{label}</label>
      <input
        className="rv-input"
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => setter(e.target.value)}
        disabled={saving}
      />
    </div>
  );

  return (
    <div style={{ border: '1px solid var(--cp-border)', borderRadius: 'var(--radius)', padding: '0.75rem', marginBottom: '1rem' }}>
      {error && <div className="rv-error">{error}</div>}
      {client.isEntity ? (
        <div>
          <label className="rv-label">Name</label>
          <input
            className="rv-input"
            type="text"
            value={entityName}
            onChange={(e) => setEntityName(e.target.value)}
            disabled={saving}
          />
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            {field('Surname', surname, setSurname)}
            {field('Initials', initials, setInitials)}
            {field('Nickname', nickname, setNickname)}
          </div>
          {isManual && (
            <div style={{ marginTop: '0.6rem' }}>
              <label className="rv-label">Display name</label>
              {structured ? (
                <div className="rv-note-info" style={{ marginTop: 0 }}>
                  Derived from the fields above: <strong>{derivedPreview}</strong>
                </div>
              ) : (
                <input
                  className="rv-input"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={saving}
                />
              )}
            </div>
          )}
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
        <button className="rv-btn rv-btn-outline" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="rv-btn rv-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving\u2026' : 'Save name'}
        </button>
      </div>
    </div>
  );
}

export default function ClientDetailModal({
  client,
  groupLabel = null, // set when the client belongs to a group (§34.10)
  onClose,
  onChangeMonth, // (client, newMonth) => void — Adviser-manual month move
  onPatch, // (client, patch) => void — optimistic checklist persistence
  onDelete, // (client) => void
  onAddToPipeline, // (client) => void — planned → submitted (§34.9)
  onSaveName, // async (client, patch) => boolean — per-client name save
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [savingName, setSavingName] = useState(false);

  if (!client) return null;

  return (
    <div className="rv-overlay" onClick={onClose}>
      <div className="rv-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Client review details">
        <h3 className="rv-modal-title" style={{ marginBottom: '0.5rem' }}>
          {client.isEntity && <Building2 size={17} title="Entity" />}
          {formatClientListName(client)}
          {onSaveName && !editingName && (
            <button
              type="button"
              className="rv-group-rename"
              title="Edit name"
              aria-label="Edit name"
              onClick={() => setEditingName(true)}
            >
              <Pencil size={13} />
            </button>
          )}
        </h3>

        {editingName && (
          <NameEditor
            client={client}
            saving={savingName}
            onCancel={() => setEditingName(false)}
            onSave={async (patch) => {
              setSavingName(true);
              const ok = await onSaveName(client, patch);
              setSavingName(false);
              if (ok) setEditingName(false);
            }}
          />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          <span
            className="rv-status-chip"
            style={STATUS_CHIP_STYLES[client.status] || STATUS_CHIP_STYLES.planned}
          >
            {REVIEW_STATUS_LABELS[client.status] || client.status}
          </span>
          {/* Ad-hoc pull into the pipeline (§34.9) — same planned →
              submitted transition as the nudge, for a specific client
              regardless of the pacing suggestion. */}
          {client.status === 'planned' && onAddToPipeline && (
            <button
              className="rv-btn rv-btn-primary"
              style={{ padding: '0.3rem 0.65rem', fontSize: '0.72rem' }}
              onClick={() => onAddToPipeline(client)}
            >
              <CalendarPlus size={13} /> Add to pipeline
            </button>
          )}
          {/* Derived at render time — never persisted (§34.4). */}
          {isReviewOverdue(client, getCurrentSASTYearMonth()) && (
            <span className="rv-tag rv-tag-overdue">OVERDUE</span>
          )}
          {groupLabel && (
            <span className="rv-tag rv-tag-entity" title="Member of a review group">
              GROUP: {groupLabel.toUpperCase()}
            </span>
          )}
        </div>

        <div className="rv-field">
          <label className="rv-label" htmlFor="rv-detail-month">Review month ({client.reviewYear})</label>
          <select
            id="rv-detail-month"
            className="rv-select"
            value={client.reviewMonth}
            onChange={(e) => onChangeMonth(client, Number(e.target.value))}
          >
            {OPEN_MONTHS.map((m) => (
              <option key={m} value={m}>{MONTH_NAMES[m]}</option>
            ))}
          </select>
          {groupLabel && (
            <div className="rv-note-info">
              Moving this client moves the whole "{groupLabel}" group —
              group members always share one review month.
            </div>
          )}
        </div>

        <div className="rv-field">
          <label className="rv-label">Checklist</label>
          <div style={{ border: '1px solid var(--cp-border)', borderRadius: 'var(--radius)', padding: '0 0.5rem' }}>
            {/* Entities: exactly two items — Annual Review + Commission
                Splits; the S14 / LA rows (and their applicability
                toggles) never render for an entity (§34.10). */}
            {CHECKLIST_ITEMS.filter(
              (item) => !(client.isEntity && item.applicableField)
            ).map((item) => (
              <ChecklistRow
                key={item.key}
                item={item}
                client={client}
                onPatch={(patch) => onPatch(client, patch)}
              />
            ))}
          </div>
        </div>

        <div className="rv-modal-actions" style={{ justifyContent: 'space-between' }}>
          {confirmingDelete ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--cp-danger)', fontWeight: 600 }}>
                Remove {formatClientListName(client)} from the planner?
              </span>
              <button className="rv-btn rv-btn-danger" onClick={() => onDelete(client)}>
                Yes, remove
              </button>
              <button className="rv-btn rv-btn-outline" onClick={() => setConfirmingDelete(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <>
              <button className="rv-btn rv-btn-outline" onClick={() => setConfirmingDelete(true)}>
                <Trash2 size={14} /> Remove from planner
              </button>
              <button className="rv-btn rv-btn-primary" onClick={onClose}>Done</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
